import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Save } from 'lucide-react'
import { type FormEvent, useEffect, useId, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import type { ClientLite } from '@/components/planning/planning-types'
import { useApi } from '@/hooks/use-api'
import { ApiError } from '@/lib/api'

/**
 * Create / edit dialog for the Clients page.
 *
 * Mirrors the planning dialog conventions — `client === null`
 * means create, `client === <row>` means edit, `client ===
 * undefined` means closed.
 *
 * Validation
 * ----------
 *   - `name`  required, trimmed.
 *   - `email` optional, but if filled must look like an email
 *     (the API double-validates).
 *   - `siret` optional, but if filled must be exactly 14
 *     digits (legal SIRET length, INSEE rule). We don't run
 *     the Luhn check here — the API can refuse if it ever
 *     enforces it, and we don't want to block on a typo while
 *     the admin is looking at a paper invoice.
 *   - `color` defaults to `#3498db` (the V1 blue) for new
 *     clients; preserved for edits.
 *
 * Submit invalidates the `['clients']` cache namespace so any
 * page using this data (the Clients listing AND the planning /
 * missions pages) refreshes on success.
 */

interface ClientDialogProps {
  client: ClientLite | null | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface FormState {
  name: string
  email: string
  phone: string
  address: string
  siret: string
  color: string
}

interface FormErrors {
  name?: string
  email?: string
  siret?: string
}

const DEFAULT_COLOR = '#3498db'

const EMPTY_FORM: FormState = {
  name: '',
  email: '',
  phone: '',
  address: '',
  siret: '',
  color: DEFAULT_COLOR,
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u
const SIRET_REGEX = /^\d{14}$/u

interface ApiClientFull extends ClientLite {
  phone: string | null
  address: string | null
  siret: string | null
}

export function ClientDialog({ client, open, onOpenChange }: ClientDialogProps) {
  const api = useApi()
  const queryClient = useQueryClient()

  const titleId = useId()
  const nameId = useId()
  const emailId = useId()
  const phoneId = useId()
  const addressId = useId()
  const siretId = useId()
  const colorId = useId()

  const isEdit = client !== undefined && client !== null

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Reload form when the dialog opens. Edits hydrate from the
  // row; a fresh `null` resets to the empty form.
  useEffect(() => {
    if (!open) return
    if (client !== undefined && client !== null) {
      // The list endpoint returns a lite shape; fetching the
      // full row to populate `phone` / `address` / `siret` is
      // unnecessary because the list itself includes them via
      // `clientsService` — we just normalise nulls to empty
      // strings here.
      const full = client as ApiClientFull
      setForm({
        name: full.name,
        email: full.email ?? '',
        phone: full.phone ?? '',
        address: full.address ?? '',
        siret: full.siret ?? '',
        color: full.color || DEFAULT_COLOR,
      })
    } else {
      setForm(EMPTY_FORM)
    }
    setErrors({})
    setSubmitError(null)
  }, [open, client])

  const saveMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const payload = buildPayload(form)
      if (isEdit) {
        await api.patch<unknown>(`/api/clients/${client.id}`, payload)
      } else {
        await api.post<unknown>('/api/clients', payload)
      }
    },
    onSuccess: async () => {
      // Both `['clients', 'lite']` (used by planning + missions)
      // and `['clients', 'list', ...]` (used by this page) live
      // under the `clients` namespace.
      await queryClient.invalidateQueries({ queryKey: ['clients'] })
      onOpenChange(false)
    },
    onError: (err) => {
      setSubmitError(
        err instanceof ApiError
          ? err.message
          : "Impossible d'enregistrer le client.",
      )
    },
  })

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    const validation = validate(form)
    setErrors(validation)
    if (Object.keys(validation).length > 0) return
    setSubmitError(null)
    saveMutation.mutate()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" aria-labelledby={titleId}>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle id={titleId}>
              {isEdit ? 'Modifier le client' : 'Nouveau client'}
            </DialogTitle>
            <DialogDescription>
              {isEdit
                ? 'Modifiez les informations puis enregistrez.'
                : 'Ajoutez un nouveau client à votre annuaire.'}
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 flex flex-col gap-1.5">
              <Label htmlFor={nameId}>Nom *</Label>
              <Input
                id={nameId}
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                invalid={errors.name !== undefined}
                placeholder="Nom du client"
              />
              {errors.name !== undefined ? (
                <p className="text-xs text-danger">{errors.name}</p>
              ) : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor={emailId}>Email</Label>
              <Input
                id={emailId}
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                invalid={errors.email !== undefined}
                placeholder="contact@client.fr"
              />
              {errors.email !== undefined ? (
                <p className="text-xs text-danger">{errors.email}</p>
              ) : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor={phoneId}>Téléphone</Label>
              <Input
                id={phoneId}
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="01 23 45 67 89"
              />
            </div>

            <div className="sm:col-span-2 flex flex-col gap-1.5">
              <Label htmlFor={addressId}>Adresse</Label>
              <Textarea
                id={addressId}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                rows={2}
                placeholder="Adresse postale (optionnel)"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor={siretId}>SIRET</Label>
              <Input
                id={siretId}
                type="text"
                inputMode="numeric"
                maxLength={14}
                value={form.siret}
                onChange={(e) =>
                  setForm({ ...form, siret: e.target.value.replace(/\D/g, '') })
                }
                invalid={errors.siret !== undefined}
                placeholder="14 chiffres"
              />
              {errors.siret !== undefined ? (
                <p className="text-xs text-danger">{errors.siret}</p>
              ) : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor={colorId}>Couleur</Label>
              <div className="flex items-center gap-3">
                <input
                  id={colorId}
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  // Native color picker — desktop browsers
                  // open the OS picker, which is more than
                  // enough for the MVP.
                  className="h-10 w-14 rounded-md border border-surface-border bg-surface cursor-pointer"
                />
                <span
                  aria-hidden="true"
                  className="w-6 h-6 rounded-full border border-surface-border shrink-0"
                  style={{ background: form.color }}
                />
                <span className="font-mono text-xs text-text-muted">
                  {form.color.toUpperCase()}
                </span>
              </div>
            </div>

            {submitError !== null ? (
              <p
                role="alert"
                className="sm:col-span-2 text-sm text-danger text-center font-medium"
              >
                {submitError}
              </p>
            ) : null}
          </DialogBody>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Annuler
              </Button>
            </DialogClose>
            <Button type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? (
                <Spinner label="Enregistrement" />
              ) : (
                <>
                  <Save size={16} />
                  {isEdit ? 'Enregistrer' : 'Créer'}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/* -------------------------------------------------------------------------- */
/*  Validation + payload                                                       */
/* -------------------------------------------------------------------------- */

function validate(form: FormState): FormErrors {
  const errors: FormErrors = {}
  if (form.name.trim() === '') errors.name = 'Le nom est obligatoire.'
  if (form.email !== '' && !EMAIL_REGEX.test(form.email)) {
    errors.email = 'Email invalide.'
  }
  if (form.siret !== '' && !SIRET_REGEX.test(form.siret)) {
    errors.siret = 'Le SIRET doit contenir 14 chiffres.'
  }
  return errors
}

interface ClientPayload {
  name: string
  color: string
  email: string | null
  phone: string | null
  address: string | null
  siret: string | null
}

function buildPayload(form: FormState): ClientPayload {
  return {
    name: form.name.trim(),
    color: form.color || DEFAULT_COLOR,
    // Empty strings → null so we don't store "" rows the API
    // would later report as present-but-empty.
    email: form.email.trim() === '' ? null : form.email.trim().toLowerCase(),
    phone: form.phone.trim() === '' ? null : form.phone.trim(),
    address: form.address.trim() === '' ? null : form.address.trim(),
    siret: form.siret.trim() === '' ? null : form.siret.trim(),
  }
}
